import { Heading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { PlusCircleIcon } from '@mm/icons';
import suggestionsCRUDActions from '@mm/state/actions/SuggestionsCRUDActions';
import triggerCRUDActions, {
  CreateTriggerParams,
} from '@mm/state/actions/TriggerCRUDActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { defaultPageIdSelector } from '@mm/state/selectors/pageSelectors';
import { qAndATriggersSelector } from '@mm/state/selectors/triggerSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as QnAIcon } from 'assets/feature-svgs/q-and-a.svg';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimarySelectWithPush } from '../../../../../common/components/PrimarySelect/PrimarySelectWithPush/PrimarySelectWithPush';
import { SearchInput } from '../../../../../common/components/SearchInput/SearchInput';
import { ITEMS } from '../../../../../common/constants/items';
import { useResolutionInfo, getResolutionInfo } from '@mm/hooks';
import { SIDEBAR_MENU } from '../../../../../layout/BotBuilderSidebar/sidebar-menu';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { TriggerCatchAll } from '../../components/TriggerCatchAll/TriggerCatchAll';
import { TriggerGrid } from '../../components/TriggerGrid/TriggerGrid';
import { TriggerSidePane } from '../../components/TriggerSidePane/TriggerSidePane';
import TriggerEntity from '../../requestModels/TriggerEntity';
import './TriggerPanel.scss';
import { InactiveChannelAlertSmart } from '../../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';

export const TriggerPanel = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const getAllTriggers = (chatbotId: number) =>
    dispatch(triggerCRUDActions.retrieveAll({ botId: chatbotId }));
  const getSuggestions = (chatbotId: number) =>
    dispatch(suggestionsCRUDActions.retrieveAll(chatbotId));
  const create = (chatbotId: number, entity: CreateTriggerParams) =>
    dispatch(triggerCRUDActions.create(chatbotId, entity));
  const unsetTriggers = () => dispatch(triggerCRUDActions.unsetTriggers());
  const openUpgradeToProModal = (landmineName: string) =>
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        planFallback: PageBillingType.Pro,
      }),
    );

  const triggers = useSelector(qAndATriggersSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const defaultDialogueId = useSelector(defaultPageIdSelector);
  const unlimitedTriggersFeatured = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).features.unlimitedTriggers;

  const [query, setQuery] = useState('');

  const hasNoTrigger = triggers.length === 0;

  useEffect(() => {
    if (chatbotId && chatbotId !== -1) {
      getSuggestions(chatbotId);
      getAllTriggers(chatbotId);
    }

    return () => {
      unsetTriggers();
    };
  }, [chatbotId]);

  const createTrigger = () => {
    const isDisabled = !unlimitedTriggersFeatured && triggers.length > 0;

    if (isDisabled) {
      openUpgradeToProModal(landmineNames['TRIGGERS__CREATE-TRIGGER']);
    } else {
      const maxSortOrder = Math.max(...triggers.map(t => t.sortOrder), 0);
      const entity = TriggerEntity.make(
        chatbotId,
        maxSortOrder < 0 ? 0 : maxSortOrder + 1,
        defaultDialogueId,
      );
      create(chatbotId, { ...entity, eventType: 'session_start' });
    }
  };

  const onChangeQuery = (e: React.ChangeEvent<HTMLInputElement>) =>
    setQuery(e.target.value);

  const { isMobile } = useResolutionInfo();
  const { screenWidth } = getResolutionInfo();
  const menuOptions = SIDEBAR_MENU.filter(menu => menu.id === 'CHATBOT')[0]
    ?.menuItems;

  return (
    <>
      {isMobile && screenWidth < 600 && (
        <PrimarySelectWithPush
          options={menuOptions}
          value={menuOptions[2]}
          headerText="Questions & Answers"
          customDropdownStyles={{ zIndex: '99' }}
          css={{ select: 'trigger-panel__mobile-menu-select' }}
        />
      )}
      <div className="trigger-panel">
        <div className="trigger-panel__header-padding" />
        <div className="trigger-panel__inner-container">
          <InactiveChannelAlertSmart
            facebook
            sms
            webChat
            instagram
            type="Q&A"
            verb="triggered for all {channel} contacts"
          />
          <div className="trigger-panel__actions-header">
            <div className="trigger-panel__actions-header--search">
              <SearchInput
                value={query}
                onChange={onChangeQuery}
                placeholder="Search"
                className="trigger-panel__search"
              />
              <PrimaryButton
                className="create-chat-starter-button"
                onClick={createTrigger}
                leftIcon={<PlusCircleIcon />}
                css={{ leftIcon: 'trigger-panel__plus-circle' }}
              >
                Create {ITEMS.QA}
              </PrimaryButton>
            </div>
            <div className="trigger-panel__actions-header--settings">
              <NeedHelpLabel
                style={{ marginLeft: '15px' }}
                link="https://mobilemonkey.com/help/article/ebz3gg5w7t-q-a-overview"
              />
            </div>
          </div>
          {hasNoTrigger && (
            <div className="trigger-panel__empty-container">
              <TriggerCatchAll className="trigger-panel__empty-state-catch-all" />
              <div className="trigger-panel__chat-starters-empty-state">
                <div className="chat-starters-empty-state__icon-container">
                  <QnAIcon
                    className="trigger-panel__chat-starters-empty-state__icon"
                    color="#e7e7e9"
                    width="16"
                  />
                </div>
                <Heading
                  size={6}
                  className="trigger-panel__chat-starters-empty-state__text"
                  text="You don't have any Q&A yet"
                />
                <PrimaryButton
                  onClick={createTrigger}
                  leftIcon={<PlusCircleIcon />}
                  className="create-chat-starter-button"
                  css={{ leftIcon: 'trigger-panel__plus-circle' }}
                >
                  Create {ITEMS.QA}
                </PrimaryButton>
              </div>
            </div>
          )}

          {!hasNoTrigger && (
            <div className="trigger-panel__content-container">
              <div className="trigger-panel__grid trigger-panel__scrollable">
                <TriggerGrid query={query} createTrigger={createTrigger} />
              </div>

              {!isMobile && (
                <div className="trigger-panel__sidebar trigger-panel__scrollable">
                  <TriggerSidePane />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
